import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Eagerly bundle every locale JSON. Vite expands this glob at build time, so
// adding a new namespace file under packages/i18n/locales/{lang}/{ns}.json
// picks it up automatically without touching this file.
const heModules = import.meta.glob('@lexcrm/i18n/locales/he/*.json', {
  eager: true,
}) as Record<string, { default: Record<string, unknown> }>;
const enModules = import.meta.glob('@lexcrm/i18n/locales/en/*.json', {
  eager: true,
}) as Record<string, { default: Record<string, unknown> }>;

function buildResources(modules: Record<string, { default: Record<string, unknown> }>) {
  const out: Record<string, Record<string, unknown>> = {};
  for (const [path, mod] of Object.entries(modules)) {
    const ns = path.split('/').pop()!.replace(/\.json$/, '');
    out[ns] = mod.default;
  }
  return out;
}

const resources = {
  he: buildResources(heModules),
  en: buildResources(enModules),
};

export const SUPPORTED_LANGS = ['he', 'en'] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

function applyHtmlDir(lang: string) {
  const dir = lang === 'he' ? 'rtl' : 'ltr';
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  }
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    // Hebrew is the primary audience (Israeli law firms), so anyone who
    // hasn't explicitly chosen English gets Hebrew. Skip navigator-language
    // detection — it would otherwise flip English-on-laptop users to en
    // before they had a chance to express a preference.
    fallbackLng: 'he',
    supportedLngs: SUPPORTED_LANGS as unknown as string[],
    nonExplicitSupportedLngs: true,
    ns: Object.keys(resources.he),
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage'],
      lookupLocalStorage: 'lexcrm.lang',
      caches: ['localStorage'],
    },
  });

// Keep <html lang|dir> in sync with the active language, both on init and
// every subsequent change. LanguageDetector resolves the initial lang
// synchronously during init() above.
applyHtmlDir(i18n.resolvedLanguage ?? i18n.language ?? 'he');
i18n.on('languageChanged', (lang) => applyHtmlDir(lang));

export default i18n;
